/**
 * Hover card for a Color By CREW legend entry: each seat and the sailor in it.
 * Portaled to document.body so the page scale transform does not shift it off the cursor.
 */
import { createEffect, createSignal, For, onCleanup, Show, type Accessor } from "solid-js";
import { Portal } from "solid-js/web";
import { persistantStore } from "../../store/persistantStore";
import { CREW_CODE_UNMATCHED, describeCrew } from "../../utils/crewCode";
import { loadCrewDirectory, type CrewDirectory } from "../../utils/crewDirectory";

export interface CrewLegendTip {
  x: number;
  y: number;
  name: string;
  lines: Array<{ label: string; name: string }> | null;
  ready: boolean;
}

export interface CrewLegendHover {
  tip: Accessor<CrewLegendTip | null>;
  show: (event: { clientX: number; clientY: number }, name: string) => void;
  move: (event: { clientX: number; clientY: number }) => void;
  /** Delayed so moving from a swatch onto its label does not drop the card. */
  hide: () => void;
  dismiss: () => void;
}

function place(event: { clientX: number; clientY: number }): { x: number; y: number } {
  const margin = 12;
  let x = event.clientX + 14;
  let y = event.clientY + 16;
  if (typeof window !== "undefined") {
    if (x + 240 > window.innerWidth) {x = Math.max(margin, event.clientX - 240);}
    if (y + 180 > window.innerHeight) {y = Math.max(margin, event.clientY - 180);}
  }
  return { x, y };
}

function fallbackText(name: string, ready: boolean): string {
  if (!ready) {return "Loading crew…";}
  const key = name.trim().toUpperCase();
  if (!key || key === "NONE") {return "No crew code on these points.";}
  if (key === CREW_CODE_UNMATCHED) {return "This line-up did not match a saved crew.";}
  return "No saved crew matches this code.";
}

/**
 * Load the project crews while Color By is CREW, and track the legend entry under the pointer.
 * Call during component setup. `active` is read inside an effect, so pass a function.
 */
export function createCrewLegendHover(active: () => boolean): CrewLegendHover {
  const [tip, setTip] = createSignal<CrewLegendTip | null>(null);
  let directory: CrewDirectory | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const dismiss = () => {
    if (hideTimer !== undefined) {
      clearTimeout(hideTimer);
      hideTimer = undefined;
    }
    setTip(null);
  };
  onCleanup(() => {
    if (hideTimer !== undefined) {clearTimeout(hideTimer);}
  });

  createEffect(() => {
    if (!active()) {
      directory = null;
      dismiss();
      return;
    }
    const className = persistantStore.selectedClassName();
    const projectId = Number(persistantStore.selectedProjectId());
    if (!className || !Number.isFinite(projectId) || projectId <= 0) {
      directory = null;
      return;
    }
    let cancelled = false;
    onCleanup(() => { cancelled = true; });
    void loadCrewDirectory(className, projectId).then((data) => {
      if (cancelled) {return;}
      directory = data;
      setTip((prev) => {
        if (!prev) {return prev;}
        return { ...prev, ready: true, lines: describeCrew(prev.name, data.crews, data.roster) };
      });
    });
  });

  const show = (event: { clientX: number; clientY: number }, name: string) => {
    if (hideTimer !== undefined) {
      clearTimeout(hideTimer);
      hideTimer = undefined;
    }
    const { x, y } = place(event);
    const lines = directory ? describeCrew(name, directory.crews, directory.roster) : null;
    setTip({ x, y, name, lines, ready: directory != null });
  };

  const move = (event: { clientX: number; clientY: number }) => {
    const { x, y } = place(event);
    setTip((prev) => (prev ? { ...prev, x, y } : prev));
  };

  const hide = () => {
    if (hideTimer !== undefined) {clearTimeout(hideTimer);}
    hideTimer = setTimeout(() => {
      hideTimer = undefined;
      setTip(null);
    }, 120);
  };

  return { tip, show, move, hide, dismiss };
}

export default function CrewLegendTooltip(props: { tip: Accessor<CrewLegendTip | null> }) {
  return (
    <Show when={props.tip()}>
      <Portal mount={typeof document !== "undefined" ? document.body : undefined}>
        <div
          class="tooltip crew-legend-tooltip"
          role="tooltip"
          style={{ left: `${props.tip()?.x ?? 0}px`, top: `${props.tip()?.y ?? 0}px` }}
        >
          <Show
            when={props.tip()?.ready && props.tip()?.lines}
            fallback={
              <table>
                <tbody>
                  <tr>
                    <td>{fallbackText(props.tip()?.name ?? "", Boolean(props.tip()?.ready))}</td>
                  </tr>
                </tbody>
              </table>
            }
          >
            <table>
              <tbody>
                <For each={props.tip()?.lines ?? []}>
                  {(line) => (
                    <tr>
                      <td>{line.label}</td>
                      <td>{line.name}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </div>
      </Portal>
    </Show>
  );
}
